import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@mui/material';
import StyledTextField from '../styled-components/StyledTextField';
import { PurposeOption } from './usePurposes';
import { normalize } from '../../utils';
import { scrollLock } from '../entity-editor/entity-editor-blocks/GeneralFieldsBlock';

type PropsPurposeAutocomplete = {
  value: string;
  options: PurposeOption[];
  loading:boolean;
  error: string | null;
  onInputChange: (value: string) => void;
  onValueChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  onInvalidInput?: (message: string) => void;
  variant?: 'filled' | 'outlined' | 'standard' | undefined;
  freeSolo?: boolean | undefined;
}

export const isAllowedPurpose = (purpose: string, options: PurposeOption[]) => {
  const normalized = normalize(purpose);
  return options.some((option) => normalize(option.value) === normalized);
};

const VALID_REGEX = /^[A-Za-zZàáâäæãåāăąçćčďđèéêëēėęěğǵḧîïíīįıłḿñǹńňôöòóœøōõőṕŕřśšşťțûüùúūǘůűųẃẍÿýžźżÀÁÂÄÆÃÅĀĂĄÇĆČĎĐÈÉÊËĒĖĘĚĞĠḦÎÏÍĪĮİŁḾÑǸŃŇÔÖÒÓŒØŌÕŐṔŔŘŚŠŞŤȚÛÜÙÚŪǗŮŰŲẂẌŸÝŽŹŻ ,.\-`'()"]*$/;

const PurposeAutocomplete: React.FC<PropsPurposeAutocomplete> = ({
  value,
  options,
  loading,
  error,
  onInputChange,
  onValueChange,
  label,
  disabled,
  onInvalidInput,
  variant,
  freeSolo = false,
}) =>{
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(value ?? "");
  const showInvalidRef = useRef<boolean>(false);

  useEffect(() => {
    scrollLock(isOpen);
    return () => {
      scrollLock(false);
    };
  }, [isOpen]);

  useEffect(() => {
    const next = value ?? "";
    setInputValue((prev) => (prev === next ? prev : next));
  }, [value]);
  const warningOnce = (message: string) => {
    if (showInvalidRef.current) return;
    if (onInvalidInput) {
      onInvalidInput(message);
    }
    showInvalidRef.current = true;
  };

  const appearanceOptions = inputValue.trim().length >= 3 ? options : [];
  const inputChange = (valueInput: string) => {
    const normalized = normalize(valueInput);
    if (!normalized) {
      onValueChange("");
      setInputValue("");
      return;
    }
    if (isAllowedPurpose(normalized, options)) {
      onValueChange(normalized);
      setInputValue(normalized);
      showInvalidRef.current = false;
      return;
    }
    warningOnce("The Purpose field can be changed to a value from the list. The old value can be retained, but custom text is not allowed.");
    setInputValue(value ?? "");
  }

  const handleBlur = (params:any) =>
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>)=> {
    if (params.inputProps && typeof params.inputProps.onBlur === "function") {
      params.inputProps.onBlur(event);
    }
    if (!freeSolo) {
      // const trimmedInputValue = inputValue.trim();
      if (!normalize(inputValue)) return;
      // const exists = options.some((option) => option.value === normalize(inputValue));
      if (!isAllowedPurpose(inputValue, options)) {
        warningOnce("Select the purpose of the object from the list by first typing part of the word.");
        setInputValue("");
        onValueChange("");
      }
    } else {
      inputChange(inputValue);
    }
  };

  return (
    <>
      <Autocomplete <PurposeOption, false, false, boolean>
        freeSolo={freeSolo}
        disabled={disabled}
        options={appearanceOptions}
        value={appearanceOptions.find((option) => normalize(option.value)=== normalize(value || "")) ?? null}
        // value={value || ""}
        inputValue={inputValue}
        loading={loading}
        loadingText="Loading..."
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        getOptionLabel={(option: any) =>
          typeof option === "string" ? option : option.value
        }
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        onInputChange={(_, newInput, reason) => {
          if (reason === "clear") {
            setInputValue("");
            onInputChange("");
            showInvalidRef.current = false;
            return;
          }
          if (reason !== "input") return;
          if (newInput === "") {
            setInputValue("");
            onInputChange("");
            showInvalidRef.current = false;
            return;
          }
          if (!VALID_REGEX.test(newInput)) {
            warningOnce("Only Latin and Dutch letters, spaces, periods, commas, hyphens and quotation marks are allowed.");
            return;
          }
          showInvalidRef.current = false;
          setInputValue(newInput);
          onInputChange(newInput);
          // if (newInput === "" || VALID_REGEX.test(newInput)) {
          //   onInputChange(newInput);
          // } else {
          //   warningOnce("Only Latin and Dutch letters, spaces, periods, commas, hyphens and quotation marks are allowed.");
          // }
        }}
        onChange={(_, newValue: any, reason) => {
          if (reason === "clear") {
            onValueChange("");
            setInputValue("");
            showInvalidRef.current = false;
            return;
          }
          if (!newValue) {
            return;
          }
          if (typeof newValue === "string") {
            if (freeSolo) {
              inputChange(newValue);
            } else {
              warningOnce("Select the purpose of the object from the list by first typing part of the word.");
            }
            return;
          }
          const next = newValue.value ?? "";
          setInputValue((prev) => (prev === next ? prev : next));
          onValueChange(next);

        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            fullWidth
            margin="dense"
            variant={variant ?? "outlined"}
            label={label}
            error={Boolean(error)}
            onBlur={handleBlur(params)}
          />
        )}
      />
    </>
  )
}

export default PurposeAutocomplete;